<template>
  <div>
    <b-navbar id="header" type="light" variant="light" sticky>
      <b-navbar-nav>
        <h4 class="mb-0">{{title}}</h4>
      </b-navbar-nav>

      <b-navbar-nav class="ml-auto">
        <b-row>
          <b-col cols="auto">
            <b-button id="view-button" size="sm" variant="warning" @click="toggleView">
              <font-awesome-icon :icon="viewMode === 'table' ? 'th-large' : 'list'"/>
            </b-button>
            <b-tooltip target="view-button" triggers="hover" variant="warning" noninteractive>
              {{ viewMode === 'table' ? 'Канбан' : 'Таблица' }}
            </b-tooltip>
            <b-button id="table-settings" size="sm" variant="warning" class="mr-2" v-b-toggle.sidebar-fields>
              <font-awesome-icon icon="cog"/>
            </b-button>
            <b-tooltip target="table-settings" triggers="hover" variant="warning" noninteractive>
              Колонки
            </b-tooltip>
            <b-btn v-if="canEditRecruitJobPostings()" size="sm" variant="success" :disabled="isBusy" @click="showModal(null, false)">
              <font-awesome-icon icon="plus" class="mr-1"/>
              Новая
            </b-btn>
          </b-col>
        </b-row>
      </b-navbar-nav>
    </b-navbar>

    <b-navbar variant="light">
      <b-navbar-nav>
        <filters-drawer
          :hide-search="true"
          :is-busy="isBusy"
          :filter-tags="activeFilterTags"
          @remove-tag="removeFilterTag"
          @submit="onSearch"
          @clear="onClearFilters"
        >
          <b-col cols="12">
            <label class="small text-muted">Вакансии</label>
            <vacancy-multi-select
              id="vacancy-multi-select"
              ref="vacancy_multi_select"
              @onSelect="onSelectVacancy"
            />
          </b-col>
          <b-col cols="12" class="mt-1">
            <label class="small text-muted">Дата публикации</label>
            <b-row class="align-items-center">
              <b-col>
                <b-form-input
                  id="input-start-opened-date"
                  size="sm"
                  type="date"
                  v-model.date="filters.start_opened_date"
                />
              </b-col>
              <b-col cols="auto" class="px-2">
                <span>&mdash;</span>
              </b-col>
              <b-col>
                <b-form-input
                  id="input-end-opened-date"
                  size="sm"
                  type="date"
                  v-model.date="filters.end_opened_date"
                />
              </b-col>
            </b-row>
          </b-col>
          <b-col cols="12" class="mt-1">
            <label class="small text-muted">Дата первого выхода в смену</label>
            <b-row class="align-items-center">
              <b-col>
                <b-form-input
                  id="input-start-closed-date"
                  size="sm"
                  type="date"
                  v-model.date="filters.start_closed_date"
                />
              </b-col>
              <b-col cols="auto" class="px-2">
                <span>&mdash;</span>
              </b-col>
              <b-col>
                <b-form-input
                  id="input-end-closed-date"
                  size="sm"
                  type="date"
                  v-model.date="filters.end_closed_date"
                />
              </b-col>
            </b-row>
          </b-col>
          <b-col cols="12" class="mt-1">
            <label class="small text-muted">Объекты</label>
            <location-multi-select
              id="location-multi-select"
              ref="location_multi_select"
              @onSelect="onSelectLocation"
            />
          </b-col>
          <b-col cols="12" class="mt-1">
            <label class="small text-muted">Ответственные</label>
            <user-multi-select
              id="user-multi-select-assigned"
              ref="user_multi_select_assigned"
              title="Ответственные"
              @onSelect="onSelectUserAssigned"
            />
          </b-col>
          <b-col md="6" cols="12" class="mt-1">
            <label class="small text-muted">Статус</label>
            <b-form-select
              id="input-statuses"
              size="sm"
              v-model="filters.status"
              :options="statusOptions"
            />
          </b-col>
          <b-col md="6" cols="12" class="mt-1">
            <label class="small text-muted">Сервис</label>
            <b-form-select
              id="input-provider-id"
              size="sm"
              v-model="filters.provider_id"
              :options="providerOptions"
            />
          </b-col>
        </filters-drawer>
      </b-navbar-nav>
    </b-navbar>

    <b-table
      v-if="viewMode === 'table'"
      id="recruit-job-postings"
      ref="recruit_job_postings"
      class="mb-0 text-small"
      selectable
      select-mode="single"
      selectedVariant="success"
      striped
      small
      responsive
      show-empty
      :empty-text="$t('message.common.emptyTable')"
      :busy.sync="isBusy"
      :items="items"
      :fields="renderedFields"
      no-provider-filtering
      no-provider-paging
      no-provider-sorting
      no-local-sorting
      @sort-changed="sortChanged"
      @row-dblclicked="onRowClicked"
      sticky-header
      head-variant="light"
    >
      <template #table-busy>
        <div class="text-center text-success my-2">
          <b-spinner class="align-middle"/>
          <strong>Загрузка</strong>
        </div>
      </template>

      <template #cell(assigned)="row">
        {{ getAssignedName(row.item.assigned) }}
      </template>

      <template #cell(status)="row">
        {{ getStatusText(row.item.status) }}
      </template>

      <template #cell(opened_date)="row">
        {{formatTableDate(row.item.opened_date)}}
      </template>

      <template #cell(action)="row">
        <b-dropdown size="sm" no-caret>
          <template #button-content>
            <font-awesome-icon icon="ellipsis-v"/>
          </template>
          <b-dropdown-item-button variant="success" @click.stop="showModal(row.item.id, true)">
            <font-awesome-icon class="mr-1" icon="eye"/>
            Смотреть
          </b-dropdown-item-button>
          <b-dropdown-item-button v-if="canEditRecruitJobPostings()" variant="primary" @click="showModal(row.item.id, false)">
            <font-awesome-icon icon="edit"/>
            Редактировать
          </b-dropdown-item-button>
          <b-dropdown-item-button v-if="canEditRecruitJobPostings()" variant="danger" @click="onRemove(row)">
            <font-awesome-icon icon="trash"/>
            Удалить
          </b-dropdown-item-button>
        </b-dropdown>
      </template>
    </b-table>

    <kanban
      v-if="viewMode === 'kanban'"
      :items="items"
      :columns="kanbanColumns"
      group-name="recruit-job-postings"
      @status-updated="kanbanUpdateStatus"
    >
      <template #card="{ item }">
        <b-card
          no-body
          class="kb-card p-2 bg-white shadow-sm rounded"
          @dblclick="onRowClicked(item)"
        >
          <b-row class="mb-1">
            <b-col>
              <strong class="text-primary">#{{ item.id }}</strong>
            </b-col>
            <b-col cols="4">
              <b-badge variant="white">{{ formatTableDate(item.opened_date) }}</b-badge>
            </b-col>
          </b-row>
          <b-row class="mb-2">
            <b-col>
              <strong class="text-primary">
                #{{ item.recruit_task_id }}
              </strong>
              <strong>
                {{ item.location_vacancy_name }}
              </strong>
            </b-col>
          </b-row>
          <b-row class="small text-muted mb-2">
            <b-col>
              <font-awesome-icon icon="map-marked-alt" style="color: green" class="mr-1"/>
              {{ item.location_name }}
            </b-col>
          </b-row>
          <b-row class="small text-muted mb-2">
            <b-col>
              <font-awesome-icon icon="user" class="mr-1"/>
              {{ getAssignedName(item.assigned) || 'Не назначен' }}
            </b-col>
          </b-row>
          <hr class="my-1"/>
          <b-row>
            <b-col class="d-flex justify-content-end">
              <b-button size="sm" variant="link" class="p-0" @click="onRowClicked(item)">
                Открыть
              </b-button>
            </b-col>
          </b-row>
        </b-card>
      </template>
    </kanban>

    <pagination-footer
      :total-rows="totalRows"
      :per-page.sync="perPage"
      @page-click="onPagination"
      @per-page-change="onPerPageChange"
    />

    <DynamicTables
      storage-key="recruitJobPostings_columns"
      sidebar-fields="sidebar-fields"
      :fields-model.sync="fields"
      :serviceEndField="serviceEndField"
      fieldKey="id"
    />

    <RecruitJobPosting ref="recruit_job_posting" @refresh="getItems" @modal-closed="onModalClosed"/>

  </div>
</template>

<script>
import recruitJobPostings from "../../network/recruitJobPostings";
import catalogs from "../../network/catalogs";
import users from "../../network/users";
  import helpers from "../../utilites/helpers";
  import dateFormatter from "../../utilites/dateFormatter";
  import DynamicTables from "../../utilites/commonUi/DynamicTables.vue";
  import FiltersDrawer from "../../utilites/commonUi/FiltersDrawer.vue";
  import Kanban from "../../utilites/commonUi/Kanban.vue";
  import permissions, {PERM} from "../../utilites/permissions";
  import PaginationFooter from "../../utilites/commonUi/PaginationFooter.vue";
  import pageSearchService from "../../utilites/pageSearchService";
  import draggable from 'vuedraggable';
    import LocationMultiSelect from "../Locations/LocationMultiSelect.vue";
    import UserMultiSelect from "../Users/UserMultiSelect.vue";
    import VacancyMultiSelect from "../Catalogs/Vacancies/VacancyMultiSelect.vue";
    import RecruitJobPosting from "./RecruitJobPosting.vue";

export default {
  name: "RecruitJobPostings",
  components: { PaginationFooter, Kanban, FiltersDrawer, RecruitJobPosting, draggable, UserMultiSelect, LocationMultiSelect, DynamicTables, VacancyMultiSelect },
  mixins: [
    recruitJobPostings, catalogs, users,
    helpers, dateFormatter, permissions, pageSearchService
  ],
  data() {
    return {
      title: 'Объявления',
      sortBy: 'id',
      sortDesc: true,
      items: [],
      usersList: [],
      vacanciesList: [],
      fields: [
        {
          key: 'recruit_task_id',
          label: 'id заявки',
          sortable: true,
          visible: true,
          class: 'recruitJobPostingsId'
        },
        {
          key: 'id',
          label: 'id объявления',
          sortable: true,
          visible: true,
        },
        {
          key: 'provider_name',
          label: 'Сервис',
          sortable: true,
          visible: true,
        },
        {
          key: 'assigned',
          label: 'Ответственный',
          sortable: true,
          visible: true,
        },
        {
          key: 'status',
          label: 'Статус',
          sortable: true,
          visible: true,
        },
        {
          key: 'opened_date',
          label: 'Дата публикации',
          sortable: true,
          visible: true,
        },
        // TODO ОТКЛИК
      ],
      serviceEndField: {
        key: 'action',
        label: 'Действия',
        class: 'recruitJobPostingsAction',
      },
      isBusy: false,
      filters: this.getDefaultFilters(),
      deleteRow: {
        id: null,
      },
      statusOptions: [
        { value: null, text: 'Все статусы' },
        { value: 'created', text: 'Создано' },
        { value: 'in_progress', text: 'В работе' },
        { value: 'published', text: 'Опубликовано' },
        { value: 'canceled', text: 'Отменено' },
        { value: 'done', text: 'Готово' },
      ],
      providerOptions: [
        { value: null, text: 'Все сервисы' },
        { value: 1, text: 'Авито' },
        { value: 2, text: 'HH' },
      ],
      viewMode: 'table',
      kanbanColumns: [
        { id: 'created', title: 'Создано', variant: 'secondary', items: [] },
        { id: 'in_progress', title: 'В работе', variant: 'primary', items: [] },
        { id: 'published', title: 'Опубликовано', variant: 'info', items: [] },
        { id: 'done', title: 'Готово', variant: 'success', items: [] },
        { id: 'canceled', title: 'Отменено', variant: 'danger', items: [] }
      ],
    }
  },
  async mounted() {
    await this.configureRefs();
  },
  async created() {
    this.isBusy = true;
    const query = this.$route.query;

    if (query) {
      if (query.start_opened_date) {
        this.filters.start_opened_date = query.start_opened_date;
      }

      if (query.end_opened_date) {
        this.filters.end_opened_date = query.end_opened_date;
      }

      if (query.start_closed_date) {
        this.filters.start_closed_date = query.start_closed_date;
      }

      if (query.end_closed_date) {
        this.filters.end_closed_date = query.end_closed_date;
      }

      if (query.locations) {
          const decodeLoc = decodeURIComponent(query.locations);
          this.filters.locations = decodeLoc.split(",");
      }

      if (query.assigned) {
        const decodeLoc = decodeURIComponent(query.assigned);
        this.filters.assigned = decodeLoc.split(",");
      }

      if (query.vacancies) {
        const decodeLoc = decodeURIComponent(query.vacancies);
        this.filters.vacancies = decodeLoc.split(",");
      }

      if (query.status) {
        this.filters.status = query.status;
      }

      if (query.provider_id) {
        this.filters.provider_id = query.provider_id;
      }
    }

    if (this.$route.hash) {
      this.currentPage = this.getPage(this.$route.hash);
    }

    this.vacanciesList = (await this.getVacancies({})).data.items;
    this.usersList = (await this.getUsersSearch({})).data.items;

    await this.onSearch();
  },
  computed: {
    visibleFields() {
      return this.fields.filter(field => field.visible);
    },
    renderedFields() {
      return [...this.visibleFields, this.serviceEndField];
    },
    activeFilterTags() {
      const tags = [];
      const defaults = this.getDefaultFilters();

      if (this.filters.start_opened_date || this.filters.end_opened_date) {
        tags.push({ key: 'opened_date', label: 'Дата публикации' });
      }

      if (this.filters.start_closed_date || this.filters.end_closed_date) {
        tags.push({ key: 'closed_date', label: 'Дата выхода в смену' });
      }

      if (this.filters.locations.length > 0) {
        tags.push({ key: 'location', label: `Объекты: ${this.filters.locations.length}`});
      }

      if (this.filters.status !== defaults.status) {
        const foundItem = this.statusOptions.find(s => s.value === this.filters.status);
        tags.push({ key: 'status', label: foundItem ? foundItem.text : 'Статус заявки' });
      }

      if (this.filters.assigned.length > 0) {
        tags.push({ key: 'assigned', label: `Ответственные: ${this.filters.assigned.length}`,});
      }

      if (this.filters.vacancies.length > 0) {
        tags.push({ key: 'vacancy', label: `Вакансии: ${this.filters.vacancies.length}`,});
      }

      if (this.filters.provider_id !== defaults.provider_id) {
        const foundItem = this.providerOptions.find(s => s.value === this.filters.provider_id);
        tags.push({ key: 'provider_id', label: foundItem ? foundItem.text : 'Сервис' });
      }

      return tags;
    },
  },
  methods: {
    async getItems() {
      const data = {
        page: this.currentPage,
        size: this.perPage,
        start_opened_date: this.filters.start_opened_date,
        end_opened_date: this.filters.end_opened_date,
        start_closed_date: this.filters.start_closed_date,
        end_closed_date: this.filters.end_closed_date,
        locations: this.filters.locations,
        status: this.filters.status,
        assigned: this.filters.assigned,
        vacancies: this.filters.vacancies,
        provider_id: this.filters.provider_id,
        sortBy: [this.sortBy],
        sortDesc: [this.sortDesc],
      }

      this.isBusy = true;
      const result = await this.getJobPostings(data);
      this.isBusy = false;

      if (!result || !result.data) {
        this.items = [];
        this.isBusy = false;
        this.totalRows = 0;
        this.showError("Что-то пошло не так. Попробуйте обновить страницу и повторить действие.");
        return;
      }

      this.items = result.data.items;
      this.totalRows = result.data.total;
    },
    showModal(jobPostingId, isViewonly) {
      const canEdit = this.canEditRecruitJobPostings();

      if (!jobPostingId) {
        if (!canEdit) return;

        this.$refs.recruit_job_posting.showModal(null, false);
        return;
      }

      const isView = isViewonly || !canEdit;
      const query = isView ? { mode: 'view' } : {};

      this.$router.push({
        name: 'RecruitJobPostings',
        params: { id: jobPostingId },
        query: query
      });
    },
    onRowClicked(item) {
      this.showModal(item.id, false);
    },
    onModalClosed() {
      if (this.$route.params.id) {
        this.$router.replace({
          name: 'RecruitJobPostings',
          params: { id: null },
          hash: this.$route.hash,
        }).catch(()=>{});
      }
    },
    getAssignedName(userId) {
      if (!userId) return null;

      const user = this.usersList.find(u => u.id === userId);
      if (!user) { return '' }
      return user.name;
    },
    getStatusText(statusValue) {
      const option = this.statusOptions.find(opt => opt.value === statusValue);
      return option.text;
    },
    onRemove(row) {
      if (!this.canEditRecruitJobPostings()) return;

      if (row.item) {
        this.deleteRow = row.item
      }

      const title = "Удаление объявления";
      const message = `Вы действительно хотите удалить объявление № "${this.deleteRow.id}"?`;
      this.showModalDelete(title, message, async () => {
        const result = await this.deleteJobPosting(this.deleteRow);

        if (result.error) {
          this.showToast('Ошибка при удалении.', 'Ошибка', 'danger');
        } else {
          this.showToast('Объявление удалено.', 'Успех', 'success');
          await this.getItems();
        }
      });
    },
    // Filters
    getDefaultFilters() {
      return {
        query: '',
        start_opened_date: null,
        end_opened_date: null,
        start_closed_date: null,
        end_closed_date: null,
        locations: [],
        status: null,
        assigned: [],
        vacancies: [],
        provider_id: null,
      };
    },
    async onClearFilters() {
      const filters = this.getDefaultFilters();

      if (JSON.stringify(this.filters) === JSON.stringify(filters)) return;

      this.filters = filters;
      await this.configureRefs();
      await this.onSearch();
    },
    removeFilterTag(tag) {
      if (tag.key === 'opened_date') {
        this.filters.start_opened_date = null;
        this.filters.end_opened_date = null;
      }
      else if (tag.key === 'closed_date') {
        this.filters.start_closed_date = null;
        this.filters.end_closed_date = null;
      }
      else if (tag.key === 'location') {
        this.filters.locations = [];
        this.$refs.location_multi_select.onClearSelected();
      }
      else if (tag.key === 'status') {
        this.filters.status = null;
      }
      else if (tag.key === 'assigned') {
        this.filters.assigned = [];
        this.$refs.user_multi_select_assigned.onClearSelected();
      }
      else if (tag.key === 'vacancy') {
        this.filters.vacancies = [];
        this.$refs.vacancy_multi_select.onClearSelected();
      }
      else if (tag.key === 'provider_id') {
        this.filters.provider_id = null;
      }

      this.onSearch();
    },
    async configureRefs() {
      if (this.$refs.user_multi_select_assigned != null) {
        await this.$refs.user_multi_select_assigned.configure(this.filters.assigned);
      }

      if (this.$refs.location_multi_select != null) {
        await this.$refs.location_multi_select.configure(this.filters.locations);
      }

      if (this.$refs.vacancy_multi_select != null) {
        await this.$refs.vacancy_multi_select.configure(this.filters.vacancies);
      }
    },
    onSelectLocation(locations) {
      this.filters.locations = locations.map(item => { return item.id; });
    },
    onSelectUserAssigned(users) {
      this.filters.assigned = users.map(item => { return item.id; });
    },
    onSelectVacancy(vacancies) {
      this.filters.vacancies = vacancies.map(item => { return item.id; });
    },
    // Kanban
    toggleView() {
      this.viewMode = this.viewMode === 'table' ? 'kanban' : 'table';
    },
    async kanbanUpdateStatus({ item, newStatus, oldStatus }) {
      const jobPosting = this.items.find(t => t.id === item.id);
      jobPosting.status = newStatus;

      const result = await this.putJobPosting(jobPosting);

      if (result.error || !result.data) {
        jobPosting.status = oldStatus;
        this.showError('Не удалось обновить статус.');
        await this.getItems();
      } else {
        this.showToast('Статус объявления обновлён.', 'Успех', 'success');
      }
    },
    // Permissions
    canEditRecruitJobPostings() {
      return this.hasPermission([PERM.RECRUIT_JOB_POSTING_EDIT]);
    },
  },
  watch: {
    '$route.params.id': {
      handler(newId) {
        this.$nextTick(() => {
          if (newId && this.$refs.recruit_job_posting) {
            const isViewonly = this.$route.query.mode === 'view';
            this.$refs.recruit_job_posting.showModal(newId, isViewonly);
          } else if (!newId && this.$refs.recruit_job_posting) {
            this.$refs.recruit_job_posting.hideModal();
          }
        });
      }, immediate: true
    },
  }
}
</script>

<style>
.recruitJobPostingsId {
  padding-left: 10px !important;
  width: 100px;
  text-align: center;
}

.recruitJobPostingsAction {
  width: 80px;
  padding-right: 10px !important;
  text-align: center;
}

#recruit-job-postings td {
  vertical-align: middle;
}

.kb-card:hover {
  transition: background-color 0.2s, transform 0.2s, box-shadow 0.2s;
  box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15) !important;
  transform: translateY(-3px);
  z-index: 2;
  cursor: grab;
}

.ghost-card .kb-card {
  transform: translateY(0) !important;
  box-shadow: none !important;
}
</style>



// WEBPACK FOOTER //
// src/components/Recruiting/RecruitJobPostings.vue